import React, { useRef, useState, useEffect } from 'react';
import { BookOpen, Sparkles, ClipboardList, Send, Layers, MessagesSquare, ArrowRight } from 'lucide-react';
import { features } from '../data/features';

const iconMap = {
  BookOpen,
  Sparkles,
  ClipboardList,
  Send,
  Layers,
  MessagesSquare,
};

const cardTones = ['#FBFAF7', '#F6F3EB', '#F9F7F0', '#F4F1E8'];

const FeaturesView = () => {
  const scrollRef = useRef(null);
  const [featureIndex, setFeatureIndex] = useState(0);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || features.length === 0) return;
    const onScroll = () => {
      const firstCard = el.querySelector('[data-feature-card]');
      const gap = parseFloat(getComputedStyle(el).columnGap || getComputedStyle(el).gap || '0');
      const cardWidth = (firstCard?.clientWidth || el.clientWidth * 0.88) + gap;
      const index = Math.round(el.scrollLeft / cardWidth);
      setFeatureIndex(Math.min(Math.max(0, index), features.length - 1));
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [features.length]);

  return (
    <div className="w-full min-h-full flex flex-col px-4 pt-8 pb-10 editorial-paper">
      <header className="flex-shrink-0 mb-5 px-2">
        <div className="text-center">
          <img
            src="/favicon.png"
            alt="Escarole"
            className="w-11 h-11 mx-auto mb-2 object-contain"
          />
          <h2 className="text-[40px] font-instrument leading-[0.9] tracking-[-0.01em] text-black">
            Features
          </h2>
          <p className="text-[12px] uppercase tracking-[0.16em] text-black/60 font-semibold">
            Product capabilities in one glance
          </p>
        </div>
      </header>

      <div className="flex-1 flex flex-col min-h-0">
        <div
          ref={scrollRef}
          className="flex overflow-x-auto overflow-y-visible snap-x snap-mandatory scroll-smooth w-full gap-5 px-4 py-6 [&::-webkit-scrollbar]:hidden"
          style={{
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          }}
        >
          {features.map((feature, index) => {
            const IconComponent = iconMap[feature.icon];
            const isActive = index === featureIndex;
            return (
              <div
                key={feature.title}
                data-feature-card
                className={`flex-shrink-0 w-[91%] min-w-[91%] snap-center transition-all duration-300 ease-out ${
                  isActive ? 'opacity-100 translate-y-0' : 'opacity-70 translate-y-1'
                }`}
              >
                <article
                  className="editorial-card relative p-7 h-[50vh] min-h-[320px] max-h-[410px] flex flex-col"
                  style={{ backgroundColor: cardTones[index % cardTones.length] }}
                >
                  <span className="absolute inset-y-0 left-0 w-1.5 bg-black/90" aria-hidden />
                  <div className="flex items-center justify-between pb-4 border-b-2 border-black/35">
                    <p className="text-[10px] uppercase tracking-[0.22em] text-black/70 font-semibold">
                      Feature {String(index + 1).padStart(2, '0')}
                    </p>
                    {IconComponent && (
                      <span className="w-10 h-10 border-2 border-black/45 bg-white flex items-center justify-center">
                        <IconComponent className="w-5 h-5 text-black" strokeWidth={1.8} />
                      </span>
                    )}
                  </div>

                  <div className="pt-5">
                    <h3 className="text-[36px] font-instrument text-black leading-[0.96] mb-3">
                      {feature.title}
                    </h3>
                    <p className="text-[15px] text-black/75 font-geist leading-relaxed">
                      {feature.description}
                    </p>
                  </div>

                  <div className="mt-auto pt-5 border-t-2 border-black/35 flex items-center justify-end gap-2 text-black/70">
                    <span className="text-[10px] uppercase tracking-[0.2em] font-semibold">Swipe</span>
                    <ArrowRight className="w-5 h-5" strokeWidth={2.2} aria-hidden />
                  </div>
                </article>
              </div>
            );
          })}
        </div>

        <div className="flex justify-center gap-2 pt-2" aria-hidden>
          {features.map((_, i) => (
            <span
              key={i}
              className={`h-1.5 transition-colors duration-300 ${
                i === featureIndex ? 'w-8 bg-black' : 'w-4 bg-black/30'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

// FeaturesView has no props, so no PropTypes needed

export default FeaturesView;
